import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { IconButton, SvgIcon, Tooltip } from '@mui/material'
import { PencilAlt } from '@styled-icons/heroicons-outline/PencilAlt'
import { EVENT_INSTANCE_TYPES } from 'settings/constants/service'
import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import {
  selectDaysOfWeekFilledForDisplay,
  selectRecurrenceInterval,
  selectRecurrenceFrequency,
  selectRecurrencePer,
  selectRecurrence,
  selectDayOfTheWeek,
  selectDayOfTheMonth,
  selectEventOccurrence,
  selectEventInstanceType,
  selectRecurringServiceEvents,
} from 'data/configured-service/configuredServiceDetailsSelectors'

import T from 'T'
import EditServiceRecurrenceDialog from './EditServiceRecurrenceDialog'
import { mapDaysToDialogStructure } from './utils/dataTransforms'

const EditServiceRecurrence = ({ serviceId, metaFiltersData, onRecurrenceUpdate }) => {
  const [isEditRecurrenceModalOpen, setIsEditRecurrenceModalOpen] = useState(false)
  const { data } = useCSDetailsCachedData({ id: serviceId })

  const daysOfWeekRaw = useMemo(() => selectDaysOfWeekFilledForDisplay(data), [data])
  const recurrenceInterval = useMemo(() => selectRecurrenceInterval(data), [data])
  const recurrencePer = useMemo(() => selectRecurrencePer(data), [data])
  const recurrenceFrequency = useMemo(() => selectRecurrenceFrequency(data), [data])
  const recurrence = useMemo(() => selectRecurrence(data), [data])
  const dayOfTheWeek = useMemo(() => selectDayOfTheWeek(data), [data])
  const dayOfTheMonth = useMemo(() => selectDayOfTheMonth(data), [data])
  const eventOccurrence = useMemo(() => selectEventOccurrence(data) || 1, [data])
  const eventInstanceType = useMemo(() => selectEventInstanceType(data), [data])
  const recurringServiceEvents = useMemo(() => selectRecurringServiceEvents(data), [data])

  const daysStructure = useMemo(() => mapDaysToDialogStructure(daysOfWeekRaw), [daysOfWeekRaw])

  const serviceInterval = useMemo(() => {
    if (!recurrence || !recurrenceInterval || !recurrencePer) {
      return null
    }

    return {
      recurrenceInterval,
      recurrencePer,
      ...recurrence,
    }
  }, [recurrence, recurrenceInterval, recurrencePer])

  const frequency = useMemo(() => (recurrenceFrequency ? recurrenceFrequency.toString() : '1'), [recurrenceFrequency])

  const initialData = useMemo(() => {
    let initialEventOccurrence = eventOccurrence
    let initialDayOfTheWeek = dayOfTheWeek
    let initialDayOfTheMonth = dayOfTheMonth ? parseInt(dayOfTheMonth, 10) : 1

    if (recurringServiceEvents && recurringServiceEvents.length > 0) {
      if (eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK) {
        // DAY_OF_WEEK type: extract arrays of dayOfTheWeek and eventOccurrence
        initialDayOfTheWeek = recurringServiceEvents.map(event => event.dayOfTheWeek)
        initialEventOccurrence = recurringServiceEvents.map(event => event.eventOccurrence)
      } else if (eventInstanceType === EVENT_INSTANCE_TYPES.SPECIFIC_DATE) {
        // SPECIFIC_DATE type: extract array of dayOfTheMonth
        initialDayOfTheMonth = recurringServiceEvents.map(event => event.dayOfTheMonth)
      }
    }

    return {
      frequency,
      serviceInterval,
      daysStructure,
      eventInstanceType,
      eventOccurrence: initialEventOccurrence,
      dayOfTheWeek: initialDayOfTheWeek,
      dayOfTheMonth: initialDayOfTheMonth,
    }
  }, [frequency, serviceInterval, daysStructure, eventInstanceType, eventOccurrence, dayOfTheWeek, dayOfTheMonth, recurringServiceEvents])

  const handleOpenModal = useCallback(() => {
    setIsEditRecurrenceModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsEditRecurrenceModalOpen(false)
  }, [])

  return (
    <>
      <Tooltip title={T.CHANGE_RECURRENCE}>
        <IconButton size="small" onClick={handleOpenModal} sx={{ color: 'text.secondary' }}>
          <SvgIcon fontSize="small">
            <PencilAlt />
          </SvgIcon>
        </IconButton>
      </Tooltip>

      <EditServiceRecurrenceDialog
        open={isEditRecurrenceModalOpen}
        onClose={handleCloseModal}
        initialData={initialData}
        serviceId={serviceId}
        metaFiltersData={metaFiltersData}
        onRecurrenceUpdate={onRecurrenceUpdate}
      />
    </>
  )
}

EditServiceRecurrence.propTypes = {
  serviceId: PropTypes.string.isRequired,
  metaFiltersData: PropTypes.object.isRequired,
  onRecurrenceUpdate: PropTypes.func,
}

export default EditServiceRecurrence
